import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ChevronDown } from 'lucide-react';
import SciFiAlarmDashboard from './SciFiAlarmDashboard';
import AdminFloorPlans from './pages/AdminFloorPlans';

const App = () => {
  const [alarms, setAlarms] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('');
  const [expandedAnlagen, setExpandedAnlagen] = useState({});
  const [alarmsPerFacility, setAlarmsPerFacility] = useState(10);
  const [timeRange, setTimeRange] = useState(0); // 0 = all, 1 = 24h, 7 = 7 days, 30 = 30 days
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [connectionStatus, setConnectionStatus] = useState('connected');
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const previousAlarmsRef = useRef([]);
  const audioRef = useRef(null);
  const [adminView, setAdminView] = useState(null);

  // Play alert sound for critical alarms
  const playAlertSound = useCallback(() => {
    if (soundEnabled && audioRef.current) {
      audioRef.current.play().catch(e => console.log('Audio play failed:', e));
    }
  }, [soundEnabled]);

  // Check for new critical alarms
  const checkForNewCriticalAlarms = useCallback((newAlarms, oldAlarms) => {
    if (oldAlarms.length === 0) return;

    const oldIds = new Set(oldAlarms.map(a => a._id));
    const newCriticalAlarms = newAlarms.filter(alarm =>
      !oldIds.has(alarm._id) &&
      (alarm.alarmArt?.includes('Einbruch') ||
       alarm.alarmArt?.includes('Brand') ||
       alarm.alarmArt?.includes('Sabotage') ||
       alarm.alarmArt?.includes('Überfall'))
    );

    if (newCriticalAlarms.length > 0) {
      playAlertSound();
    }
  }, [playAlertSound]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch('/api/latest-alarms');
        const data = await response.json();

        checkForNewCriticalAlarms(data, previousAlarmsRef.current);
        previousAlarmsRef.current = data;

        setAlarms(data);
        setConnectionStatus('connected');
        setLastUpdate(new Date());
      } catch (error) {
        console.error('Error fetching alarms:', error);
        setConnectionStatus('disconnected');
      }
    };

    fetchData();
    const interval = setInterval(fetchData, 2000);
    return () => clearInterval(interval);
  }, [checkForNewCriticalAlarms]);

  const handleSearchChange = (value) => {
    setSearchTerm(value);
  };

  const handleFilterChange = (value) => {
    setFilterType(value);
  };

  const handleToggleAnlage = (anlageId) => {
    setExpandedAnlagen(prev => ({
      ...prev,
      [anlageId]: !prev[anlageId]
    }));
  };

  const handleAlarmsPerFacilityChange = (value) => {
    setAlarmsPerFacility(parseInt(value));
  };

  const handleTimeRangeChange = (value) => {
    setTimeRange(parseInt(value));
  };

  const handleToggleSound = () => {
    setSoundEnabled(!soundEnabled);
  };

  const handleAcknowledge = async (alarmId) => {
    try {
      await fetch(`/api/acknowledge-alarm/${alarmId}`, { method: 'POST' });
      setAlarms(alarms.map(alarm =>
        alarm._id === alarmId ? { ...alarm, acknowledged: true } : alarm
      ));
    } catch (error) {
      console.error('Error acknowledging alarm:', error);
    }
  };

  const handleAcknowledgeAll = async () => {
    try {
      await fetch('/api/acknowledge-all-alarms', { method: 'POST' });
      setAlarms(alarms.map(alarm => ({ ...alarm, acknowledged: true })));
    } catch (error) {
      console.error('Error acknowledging all alarms:', error);
    }
  };

  // Filter alarms by time range
  const filteredAlarms = timeRange === 0
    ? alarms
    : alarms.filter(alarm => {
        const alarmDate = new Date(alarm.timestamp);
        const cutoff = new Date();
        cutoff.setDate(cutoff.getDate() - timeRange);
        return alarmDate >= cutoff;
      });

  // Show admin view if active
  if (adminView?.type === 'floorplans') {
    return (
      <AdminFloorPlans
        facilityId={adminView.facilityId}
        facilityName={adminView.facilityId}
        onBack={() => setAdminView(null)}
      />
    );
  }

  return (
    <>
      {/* Hidden audio element for alert sound */}
      <audio ref={audioRef} preload="auto">
        <source src="/alert.mp3" type="audio/mpeg" />
        <source src="data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2teleRUBfLDH/7VvHgIml8Di8YQxBTeh0+bflU4jVKvX5N2NTx87qNzk46NhM0+u3ODckFwvRazb3NmPXCtHr93Y1o1bKkqu3dfViVopS7Da0dKIWSdNsNzP0IhaJk+y3MvOiFgoUbPcy8yIWCdStNzKy4dXJlKz3MrKh1cmU7TcyMqHVyZUtt3HyIZWJlW238bHhVUmVrjfxcaFVSZXut/ExYVUJli738PEhVQmWbvfwsOEUyZavN/BwoRTJlu938HCg1MmXL7fwMGDUyZdvt+/wINSJl6/377Ag1ImX8DfvcCCUiZgwN+9wIJSJmHA37y/glImYcHfvL+CUSZhwd+7voJRJmLB37q+gVEmYsLfub6BUSZjwt+5vYFRJmPC37i9gVEmZMPfuL2AUCZkw9+3vYBQJmXE37a8gFAmZcTftryAUCZmxN+1u4BPJmbE37S7f08mZ8Xfs7t/TyZnxd+yun9PJmjG36+6f08maMbfrrp+TiZpx9+tun5OJmnH36y5fk4masffq7l9TiZqx9+quX1NJmvI36m5fU0mbMjfqLh9TSZsyN+nuH1NJm3J36a4fE0mbcnfpbh8TCZuyN+kuHxMJm7J36O3fEwmb8rforf8SyZvyt+ht/xLJnDK36C2/EsmcMvfn7b7SyZxy9+etvtLJnHL35y2+0omc8zfm7X7SiZzzN+atfpKJnTN35m1+komdc3fmLT6SiZ1zd+XtPpJJnbO35a0+UkmdtDfl7P5SSZ30d+VtPlJJnjR35Sz+Ugme9LflLP5SCZ70t+Ss/hIJn3T35Gy+Egmf9TfkLL4RyaA1d+QsfhHJoHV34+x90cmgtbfj7H3RiaE2N+NsfdGJoXY34yw90Ymh9rfirD3RiaI2t+JsPZFJona34iw9kUmi9zfh6/2RSaN3d+Fr/ZEJo7d34Wv9UQmj97fhK/1RCSR39+Dr/VDJpLg34Ku9UMmk+HfgK71QyaU4t9/rvRCJpXi33+u9EIml+PffK70QiaY5N97rfRCJprk33qt80Imm+Xfeq3zQSac5d94rPNBJp3m33es80Emn+ffdar0QSag6N90qvRAJqLp33Oq9EAmounfcar0Pyak6t9wqfQ/JqXq326p8z8mpuvfbanzPyao7N9rqPM/Jqnt32qo8z4mqe3faafzPias799op/M+Jq3v32en8j4mru/fZqfyPiav8N9lpvI+JrHx32Om8j0msv" type="audio/wav" />
      </audio>
      <SciFiAlarmDashboard
        alarms={filteredAlarms}
        searchTerm={searchTerm}
        filterType={filterType}
        expandedAnlagen={expandedAnlagen}
        alarmsPerFacility={alarmsPerFacility}
        timeRange={timeRange}
        soundEnabled={soundEnabled}
        connectionStatus={connectionStatus}
        lastUpdate={lastUpdate}
        onSearchChange={handleSearchChange}
        onFilterChange={handleFilterChange}
        onToggleAnlage={handleToggleAnlage}
        onAlarmsPerFacilityChange={handleAlarmsPerFacilityChange}
        onTimeRangeChange={handleTimeRangeChange}
        onToggleSound={handleToggleSound}
        onAcknowledge={handleAcknowledge}
        onAcknowledgeAll={handleAcknowledgeAll}
        onOpenFloorplanAdmin={(facilityId) => setAdminView({ type: 'floorplans', facilityId })}
      />
    </>
  );
};

export default App;